import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';

import FormSelect from '../../../components/form/FormSelect';
import CallexLoader from '../../../components/CallexLoader';

import { followUpGetById, followUpUpdate } from '../../../store/slices/tenantSlices/dailyReportSlice';
import FormDateInput from '../../../components/form/FormDateInput';
import { parse } from 'date-fns';
import { useTimezone } from '../../../store/slices/commonSlice';
import { toZonedTime } from 'date-fns-tz';
import { formatPayloadDate } from '../../../utils/common';

const statusOptions = [
  { label: 'Pending', value: 0 },
  { label: 'Done', value: 1 },
  { label: 'Send Notification', value: 2 },
  { label: 'Agent Not Available', value: 3 },
];

const EditFollowUpModal = ({
  isOpen,
  onCloseClick,
  isEdit = false,
  editRecord = null,
  userOptions = [],
  showUser = true
}) => {
  const dispatch = useDispatch();
  const timezone = useTimezone();
  const today = toZonedTime(new Date(), timezone);
  const [isLoading, setIsLoading] = useState(false);

  const initialValues = {
    phone_number: '',
    crm_id: '',
    date_and_time: '',
    follow_up_status: '',
    process_id: '',
    user_id: ''
  };

  const validationSchema = Yup.object({
    date_and_time: Yup.string().required('date and time is required'),
    follow_up_status: Yup.number()
      .typeError('status is required')
      .required('status is required'),
    ...(showUser ? { user_id: Yup.string().required('user is required') } : {})
  });

  const onSubmit = async () => {
    try {
      const payload = {
        id: editRecord.id,
        phone_number: values.phone_number,
        crm_id: values.crm_id,
        date_and_time: formatPayloadDate(values.date_and_time),
        follow_up_status: values.follow_up_status,
        process_id: values.process_id,
        user_id: values.user_id
      };
      const response = await dispatch(followUpUpdate(payload)).unwrap();
      if (response && response?.message) {
        toast.success('Record Updated Successfully');
        onCloseClick(true);
        resetForm();
      } else {
        toast.error(response.error);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    setFieldValue,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const getFollowUpDetail = async () => {
    if (!editRecord?.id) return;
    setIsLoading(true);
    try {
      const response = await dispatch(followUpGetById(editRecord.id)).unwrap();
      const record = response?.data;
      if (record) {
        setValues({
          phone_number: record?.phone_number || '',
          crm_id: record?.crm_id || '',
          date_and_time: record?.date_and_time
            ? parse(record.date_and_time, 'yyyy-MM-dd HH:mm:ss', today)
            : '',
          follow_up_status: record?.follow_up_status ?? '',
          process_id: record?.process_id || '',
          user_id: Number(record?.user_id) || ''
        });
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getFollowUpDetail();
  }, [editRecord]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">Edit Follow Up</div>
      </ModalHeader>
      <ModalBody className="px-4">
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <CallexLoader />
          </div>
        ) : (
          <form id="crms" onSubmit={handleSubmit}>
            <Row>
              <Col lg="4" md="4" className="flex-row">
                <FormDateInput
                  name="date_and_time"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="Date"
                  showTimeSelect={true}
                  minDate={today}
                  value={values.date_and_time}
                  onChange={(e) => setFieldValue('date_and_time', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="4" md="4" className="flex-row">
                <FormSelect
                  label="Status"
                  placeholder="Select Status"
                  value={values.follow_up_status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="follow_up_status"
                  options={statusOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              {showUser && (
                <Col lg="4" md="4" className="flex-row">
                  <FormSelect
                    label="User"
                    placeholder="Select User"
                    value={values.user_id}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    name="user_id"
                    options={userOptions}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
              )}
            </Row>
          </form>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button
          form="crms"
          type="submit"
          disabled={isLoading}
          className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

EditFollowUpModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any,
  userOptions: PropTypes.array,
  showUser: PropTypes.bool
};

export default EditFollowUpModal;
